<template>
  <div class="session-search enhanced">
    <div class="search-section">
      <div class="search-container">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search albums in sessions..."
          class="search-input"
          @input="handleSearch"
          @keydown.escape="clearSearch"
          aria-label="Search albums in sessions"
          :disabled="loading"
        />
        <div class="search-actions">
          <div 
            v-if="loading"
            class="search-loading"
            aria-label="Searching..."
          >
            <div class="loading-spinner"></div>
          </div>
          <button 
            v-else-if="searchQuery"
            @click="clearSearch"
            class="search-clear"
            aria-label="Clear search"
          >
            <span>×</span>
          </button>
        </div>
      </div>
      <div v-if="searchQuery && !loading && resultCount >= 0" class="search-status">
        <span class="result-count">
          {{ resultCount }} {{ resultCount === 1 ? 'album' : 'albums' }} found
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

// Simple debounce utility
const debounce = (func, delay) => {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(null, args), delay)
  }
}

// Props
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  resultCount: {
    type: Number,
    default: 0
  },
  loading: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'search'])

// Local reactive state
const searchQuery = ref(props.modelValue)

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  searchQuery.value = newValue
})

// Debounced search function (500ms delay)
const debouncedSearch = debounce((query) => {
  emit('update:modelValue', query)
  emit('search', query)
}, 500)

// Methods
const handleSearch = () => {
  debouncedSearch(searchQuery.value)
}

const clearSearch = () => {
  searchQuery.value = ''
  emit('update:modelValue', '')
  emit('search', '')
}

// Expose methods for parent component
defineExpose({
  clearSearch
})
</script>

<style scoped>
.session-search {
  margin-bottom: 24px;
}

/* Search Section - Always Visible */
.search-section {
  margin-bottom: 16px;
}

/* Enhanced Search Input */
.search-container {
  width: 100%;
  position: relative;
}

.search-input {
  width: 100%;
  min-height: 48px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 14px 50px 14px 20px; /* Add padding for clear button */
  color: #f3f3f3;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(12px);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-input:focus {
  outline: none;
  border-color: rgba(45, 140, 240, 0.6);
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 0 0 4px rgba(45, 140, 240, 0.1), inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-input::placeholder {
  color: rgba(243, 243, 243, 0.5);
  font-weight: 400;
}

/* Search Actions Container */
.search-actions {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Clear Button */
.search-clear {
  background: transparent;
  border: none;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: rgba(243, 243, 243, 0.6);
  font-size: 20px;
  font-weight: bold;
}

.search-clear:hover {
  color: rgba(243, 243, 243, 0.9);
  transform: scale(1.2);
}

/* Loading Spinner */
.search-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(243, 243, 243, 0.3);
  border-top: 2px solid rgba(45, 140, 240, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Search Status */
.search-status {
  margin-top: 8px;
  padding: 0 4px;
}

.result-count {
  font-size: 14px;
  color: rgba(243, 243, 243, 0.7);
  font-weight: 500;
}

/* Disabled Input State */
.search-input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}


/* Mobile Responsiveness */
@media (max-width: 768px) {
  .search-input {
    min-height: 52px;
    font-size: 16px;
    padding: 16px 50px 16px 20px;
  }
  
  .search-clear {
    width: 32px;
    height: 32px;
    right: 10px;
  }
}

@media (max-width: 480px) {
  .search-input {
    padding: 18px 50px 18px 16px;
  }
}
</style>